import URI from 'jsuri';
import Q from 'q';

import API from 'js/lib/api';
import type { InjectedRouter } from 'js/lib/connectToRouter';

const jwtLoginAPI = API('/api/jwtLogins.v1/', { type: 'rest' });

type RouterLike = Pick<InjectedRouter, 'location'>;

/** The invite link is dead because the org's JWT signing key rotated. Offers a replacement link. */
export const EXPIRED_INVITE_LINK_ERROR_CODE = 'invalidWebTokenExpiredSigningKey';

/** The invite link failed to exchange for any other reason. No replacement is offered. */
export const INVALID_INVITE_LINK_ERROR_CODE = 'invalidWebToken';

export type JwtInviteCredentials = {
  organization: string;
  /** `webToken` when present, else `invitationToken`. The single source of this choice. */
  token: string;
  isWebToken: boolean;
};

export type TokenValidationSuccess = {
  id: string;
};

export type TokenValidationFailure = {
  errorCode: string;
  message: string;
};

/**
 * @deprecated Do not use. POSTs to `jwtLogins.v1` `create`, which reads the learner's identity from
 * the token's own claims and is disabled server-side for invite organizations (NTGRTNS-5024). Send
 * invite tokens of either shape to `getTokenFromInvitation` instead. Kept only so that in-flight
 * branches still compile; delete once nothing references it.
 */
export const getToken = (
  organization: string,
  webToken: string
): Q.Promise<TokenValidationSuccess | TokenValidationFailure> => {
  const data = {
    organization,
    webToken,
  };

  return Q(jwtLoginAPI.post('', { data })).then((response) => response.elements[0]);
};

/**
 * Exchanges a Coursera-issued invite token for a login ticket.
 *
 * Accepts either shape in circulation — current `invitationToken` links and legacy `webToken` ones.
 * Both signers set the token's `jti` to the invitation id, which is the only claim this endpoint
 * reads: it looks the invitation up by that id and takes the learner's name and email from the
 * invitation record, ignoring any identity the token asserts about itself.
 */
export const getTokenFromInvitation = (
  organization: string,
  invitationToken: string
): Q.Promise<TokenValidationSuccess | TokenValidationFailure> => {
  const uri = new URI().addQueryParam('action', 'loginWithInvitationToken');
  const data = {
    organization,
    invitationToken,
  };
  return Q(jwtLoginAPI.post(uri.toString(), { data }));
};

/** Must stay a POST: a GET would let link scanners and browser prefetchers trigger the email. */
export const resendJWTInviteMail = (organization: string, inviteToken: string): Q.Promise<void> => {
  const uri = new URI().addQueryParam('action', 'resendInvitationLink');
  return Q(jwtLoginAPI.post(uri.toString(), { data: { organization, inviteToken } })).then(() => undefined);
};

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

/**
 * Reads the resend identifiers off the router's query string. This is the only place the
 * `webToken || invitationToken` choice is made, so the token-exchange and the resend flow can
 * never silently diverge on which token they use.
 */
export const getJwtInviteCredentials = (router: RouterLike): JwtInviteCredentials | null => {
  const { organization, webToken, invitationToken } = router.location.query;

  if (!isNonEmptyString(organization)) return null;

  if (isNonEmptyString(webToken)) {
    return { organization, token: webToken, isWebToken: true };
  }

  if (isNonEmptyString(invitationToken)) {
    return { organization, token: invitationToken, isWebToken: false };
  }

  return null;
};

/** Whether to offer a replacement invite link, derived entirely from the router. */
export const hasExpiredInviteLink = (router: RouterLike): boolean =>
  router.location.query?.errorCode === EXPIRED_INVITE_LINK_ERROR_CODE && getJwtInviteCredentials(router) !== null;

/**
 * Exchanges whichever invite token is present in the router for a login ticket.
 *
 * NTGRTNS-5024: both `webToken` and `invitationToken` route to the invitation endpoint, never to
 * `create`. `create` reads identity from the token's own claims and is the ATO vector; the
 * invitation endpoint ignores those claims and sources identity from the invitation record instead.
 */
export const exchangeJwtInviteToken = (
  router: RouterLike
): Q.Promise<TokenValidationSuccess | TokenValidationFailure> | Promise<null> => {
  const credentials = getJwtInviteCredentials(router);
  if (!credentials) return Promise.resolve(null);

  return getTokenFromInvitation(credentials.organization, credentials.token);
};

export const resendInviteLink = async (router: RouterLike): Promise<void> => {
  const credentials = getJwtInviteCredentials(router);
  if (!credentials) return;

  await resendJWTInviteMail(credentials.organization, credentials.token);
};
